import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import toast from 'react-hot-toast';
import { createTenant, getTenant, updateTenant, getPlans, masterUploadLogo } from '../../services/api';
import { safeParseFeatures } from '../../context/AuthContext';
import PhoneInput from '../../components/PhoneInput';

// ─── Feature registry — single source of truth (visual) ─────────────────────
// IMPORTANTE: as keys aqui precisam ESTAR sincronizadas com
// backend/src/constants/planFeatures.js. Qualquer feature ativada lá precisa
// ter entrada aqui (label/icon/desc/group) para que o seletor e os badges
// mostrem texto humanizado em vez da chave crua.
export const ALL_FEATURES = {
  // ── Core ──────────────────────────────────────────────────────────────────
  appointments:    { label:'Agendamentos',             icon:'📅', desc:'Módulo de agenda e agendamentos', group:'core' },
  billing:         { label:'Faturamento',              icon:'💰', desc:'Faturas, PIX e pagamentos',       group:'core' },
  notifications:   { label:'Notificações',             icon:'🔔', desc:'Alertas e avisos internos',       group:'core' },
  public_booking:  { label:'Booking Público',          icon:'🌐', desc:'Página pública de agendamento',   group:'core' },
  // ── Integrações ───────────────────────────────────────────────────────────
  google_calendar: { label:'Google Calendar',               icon:'📆', desc:'Sincronização bidirecional com Google Agenda',      group:'integrations' },
  whatsapp:        { label:'WhatsApp',                      icon:'💬', desc:'Lembretes e confirmações automáticos via WhatsApp', group:'integrations' },
  google_review:   { label:'Link de Avaliação Google',      icon:'⭐', desc:'Envia link para avaliar no Google após o atendimento', group:'integrations' },
  // ── Experiência do cliente ────────────────────────────────────────────────
  qr_code:         { label:'QR Code de Agendamento',        icon:'📲', desc:'Gera QR Code para o cliente agendar pelo celular', group:'experiencia' },
  reschedule:      { label:'Reagendamento pelo Cliente',    icon:'🔄', desc:'Cliente pode reagendar sozinho pelo link do agendamento', group:'experiencia' },
  // ── Relacionamento e retenção ─────────────────────────────────────────────
  waitlist:        { label:'Lista de Espera',               icon:'⏳', desc:'Avisa o próximo da fila quando um horário abre', group:'retencao' },
  client_profiles: { label:'Perfil de Clientes',            icon:'👥', desc:'Histórico completo e ficha de cada cliente', group:'retencao' },
  nps:             { label:'Pesquisa de Satisfação',        icon:'📊', desc:'Envia pesquisa automática após cada atendimento', group:'retencao' },
  // ── Receita e pagamentos ──────────────────────────────────────────────────
  intake_forms:    { label:'Perguntas no Agendamento',      icon:'📋', desc:'Coleta informações do cliente antes do atendimento', group:'receita' },
  recurring:       { label:'Agendamento Recorrente',        icon:'🔁', desc:'Agenda automática para clientes fixos e períodicos', group:'receita' },
  deposits:        { label:'Sinal / Pré-pagamento (PIX)',   icon:'💳', desc:'Exige pagamento antecipado para confirmar o horário', group:'receita' },
  // ── Fidelização e vendas ──────────────────────────────────────────────────
  loyalty:         { label:'Programa de Pontos',            icon:'🏆', desc:'Clientes acumulam pontos e trocam por recompensas', group:'fidelizacao' },
  packages:        { label:'Pacotes e Vouchers',            icon:'🎁', desc:'Venda múltiplas sessões em pacote com desconto', group:'fidelizacao' },
  // ── Enterprise (registry visual — comportamento em implementação futura) ──
  api_access:      { label:'API Pública',                   icon:'🔌', desc:'Token de API REST para integrações próprias', group:'enterprise' },
  webhooks:        { label:'Webhooks',                      icon:'🪝', desc:'Notificações HTTP customizadas em eventos do sistema', group:'enterprise' },
  multi_unit:      { label:'Multi-Unidade',                 icon:'🏢', desc:'Múltiplas filiais num único cadastro', group:'enterprise' },
  whitelabel:      { label:'Whitelabel',                    icon:'🏷️', desc:'Remove a marca AtendoApp do rodapé dos e-mails e da página pública', group:'enterprise' },
  custom_domain:   { label:'Domínio Próprio',               icon:'🌍', desc:'Publicar a página pública em agenda.seunegocio.com.br', group:'enterprise' },
};

const GROUPS = {
  core:         { label:'Funcionalidades Essenciais',    color:'var(--gray-700)' },
  integrations: { label:'Integrações Externas',          color:'#0ea5e9' },
  experiencia:  { label:'Experiência do Cliente',        color:'#22c55e' },
  retencao:     { label:'Relacionamento e Retenção',     color:'#f59e0b' },
  receita:      { label:'Receita e Pagamentos',          color:'#ef4444' },
  fidelizacao:  { label:'Fidelização e Vendas',          color:'#a855f7' },
  enterprise:   { label:'Exclusivas Enterprise',         color:'#6366f1' },
};

export const DEFAULT_FEATURES = Object.fromEntries(
  Object.keys(ALL_FEATURES).map(k => [k, ['appointments','billing','notifications','public_booking','qr_code','reschedule'].includes(k)])
);

function FeatureToggle({ featureKey, meta, value, onChange }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid var(--gray-100)' }}>
      <div>
        <div style={{ fontWeight:600, fontSize:13 }}>{meta.icon} {meta.label}</div>
        <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:1 }}>{meta.desc}</div>
      </div>
      <button type="button" onClick={() => onChange(!value)} style={{
        width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
        background: value ? 'var(--primary)' : 'var(--gray-300)',
        position:'relative', transition:'background 0.2s', flexShrink:0
      }}>
        <span style={{
          position:'absolute', top:2, left: value ? 22 : 2,
          width:20, height:20, borderRadius:'50%', background:'white',
          transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)'
        }} />
      </button>
    </div>
  );
}

export function FeaturesEditor({ features, onChange, showGroups = true }) {
  const byGroup = Object.entries(ALL_FEATURES).reduce((acc, [k, meta]) => {
    (acc[meta.group] = acc[meta.group] || []).push([k, meta]);
    return acc;
  }, {});

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {Object.entries(byGroup).map(([group, items]) => (
        <div key={group}>
          {showGroups && (
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
              <span style={{ fontSize:11, fontWeight:700, color:GROUPS[group]?.color, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                {GROUPS[group]?.label}
              </span>
              <div style={{ flex:1, height:1, background:'var(--gray-100)' }} />
              <button type="button" onClick={() => {
                const allOn = items.every(([k]) => features[k]);
                const patch = Object.fromEntries(items.map(([k]) => [k, !allOn]));
                onChange({ ...features, ...patch });
              }} style={{ fontSize:10, color:'var(--primary)', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>
                {items.every(([k]) => features[k]) ? 'desmarcar todos' : 'marcar todos'}
              </button>
            </div>
          )}
          {items.map(([k, meta]) => (
            <FeatureToggle key={k} featureKey={k} meta={meta} value={!!features[k]}
              onChange={v => onChange({ ...features, [k]: v })} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function MasterTenantForm() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = !!id;

  const [plans,    setPlans]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [uploading,setUploading]= useState(false);
  const [form, setForm] = useState({
    name:'', slug:'', email:'', phone:'', cpf_cnpj:'', website:'',
    primary_color:'#2563eb', secondary_color:'#1e40af',
    plan_id:'', logo_url:'', status:'active',
    features: { ...DEFAULT_FEATURES },
    admin_name:'', admin_email:'', admin_password:'',
  });

  useEffect(() => {
    getPlans().then(r => setPlans(r.data.plans));
    if (isEdit) {
      getTenant(id).then(r => {
        const t = r.data.tenant;
        const feats = { ...DEFAULT_FEATURES, ...safeParseFeatures(t.features) };
        setForm(f => ({
          ...f, name:t.name, slug:t.slug, email:t.email, phone:t.phone||'', cpf_cnpj:t.cpf_cnpj||'', website:t.website||'',
          primary_color:t.primary_color||'#2563eb', secondary_color:t.secondary_color||'#1e40af',
          plan_id:t.plan_id||'', logo_url:t.logo_url||'', status:t.status,
          features: { ...DEFAULT_FEATURES, ...feats },
        }));
      });
    }
  }, [id, isEdit]);

  const set  = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // When plan changes, apply plan's base features but keep any extra overrides
  const handlePlanChange = async (planId) => {
    set('plan_id', planId);
    if (!planId) return;
    const plan = plans.find(p => String(p.id) === String(planId));
    if (!plan?.features) return;
    const planFeats = safeParseFeatures(plan.features);
    setForm(f => ({
      ...f,
      plan_id: planId,
      features: { ...DEFAULT_FEATURES, ...planFeats },
    }));
  };

  const handleLogoUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await masterUploadLogo(id, file);
      set('logo_url', r.data.url);
      toast.success('Logo enviada!');
    }
    catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao enviar logo');
    }
    finally { setUploading(false); }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim() || !form.email.trim())
      return toast.error('Nome, slug e email são obrigatórios');
    setLoading(true);
    try {
      const payload = { ...form, plan_id: form.plan_id || null };
      if (payload.logo_url?.startsWith('data:')) delete payload.logo_url;
      if (isEdit) {
        await updateTenant(id, payload);
        toast.success('Cliente atualizado!');
      } else {
        const res = await createTenant(payload);
        toast.success('Cliente criado! Senha temporária enviada para o e-mail do admin.');
        navigate(`/master/tenants/${res.data.tenant.id}/edit`);
      }
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setLoading(false); }
  };

  const activeCount = Object.values(form.features).filter(Boolean).length;

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>{isEdit ? 'Editar Cliente' : 'Novo Cliente'}</h2>
          <p>{isEdit ? 'Dados cadastrais da empresa' : 'Preencha para criar um novo cliente'}</p>
        </div>
        <div className="page-actions">
          {isEdit && <Link to={`/master/tenants/${id}`} className="btn btn-secondary btn-sm">← Ver detalhes</Link>}
          {!isEdit && <button className="btn btn-secondary btn-sm" onClick={() => navigate('/master/tenants')}>← Voltar</button>}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
          <div>
            {/* Dados da empresa */}
            <div className="card" style={{ marginBottom:20 }}>
              <div className="card-header"><span className="card-title">📋 Dados da Empresa</span></div>
              <div className="card-body">
                <div className="form-group">
                  <label>Nome da empresa *</label>
                  <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Empresa Ltda" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Slug * <span className="text-muted">(único)</span></label>
                    <input value={form.slug}
                      onChange={e => set('slug', e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,''))}
                      placeholder="empresa-ltda" />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Telefone</label>
                    <PhoneInput
                      value={form.phone}
                      onChange={(masked, normalized) => set('phone', normalized || masked)}
                      placeholder="(11) 91234-5678"
                    />
                  </div>
                  <div className="form-group">
                    <label>CPF / CNPJ <span style={{ fontSize:11, color:'var(--gray-400)' }}>(obrigatório pro Asaas)</span></label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.cpf_cnpj}
                      onChange={e => {
                        // Mantém só dígitos (o validator Joi do backend exige formato canônico)
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 14);
                        set('cpf_cnpj', digits);
                      }}
                      placeholder="12345678901 (CPF) ou 12345678000190 (CNPJ)"
                      maxLength={14}
                    />
                    <small style={{ fontSize:11, color:'var(--gray-500)' }}>
                      Apenas dígitos. 11 = CPF · 14 = CNPJ. Quando o reconciliador Asaas está ligado em "Config PIX",
                      tenants sem este campo caem no fluxo manual (fallback).
                    </small>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Site</label>
                    <input type="url" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://seusite.com.br" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Plano <span style={{ fontSize:11, color:'var(--gray-400)' }}>(carrega features padrão do plano)</span></label>
                    <select value={form.plan_id} onChange={e => handlePlanChange(e.target.value)}>
                      <option value="">Sem plano</option>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.name} — R$ {parseFloat(p.price).toFixed(2)}</option>)}
                    </select>
                  </div>
                </div>
                {isEdit && (
                  <div className="form-group">
                    <label>Status</label>
                    <select value={form.status} onChange={e => set('status', e.target.value)}>
                      <option value="active">Ativo</option>
                      <option value="suspended">Suspenso</option>
                      <option value="trial">Trial</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Admin — só na criação */}
            {!isEdit && (
              <div className="card" style={{ marginBottom:20 }}>
                <div className="card-header"><span className="card-title">👤 Usuário Administrador</span></div>
                <div className="card-body">
                  <div className="info-box" style={{ marginBottom:16 }}>
                    Preencha para criar automaticamente o admin do cliente. Deixe em branco para criar depois.
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nome do admin</label>
                      <input value={form.admin_name} onChange={e => set('admin_name', e.target.value)} placeholder="Nome Completo" />
                    </div>
                    <div className="form-group">
                      <label>Email do admin</label>
                      <input type="email" value={form.admin_email} onChange={e => set('admin_email', e.target.value)} placeholder="admin@empresa.com" />
                    </div>
                  </div>
                  <div className="info-box" style={{ marginTop:8, fontSize:12 }}>
                    🔑 Uma senha temporária será gerada automaticamente e enviada para o e-mail do admin.
                  </div>
                </div>
              </div>
            )}

            {/* Features */}
            <div className="card" style={{ marginBottom:20 }}>
              <div className="card-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span className="card-title">⚙️ Features Habilitadas</span>
                <span style={{ fontSize:12, color:'var(--gray-500)' }}>{activeCount}/{Object.keys(ALL_FEATURES).length} ativas</span>
              </div>
              <div className="card-body">
                <div className="info-box" style={{ marginBottom:16, fontSize:12 }}>
                  💡 Ao trocar o plano, as features são carregadas automaticamente. Você pode ajustar individualmente para este cliente.
                </div>
                <FeaturesEditor features={form.features} onChange={feats => set('features', feats)} />
              </div>
            </div>

            {/* Identidade visual */}
            <div className="card" style={{ marginBottom:20 }}>
              <div className="card-header"><span className="card-title">🎨 Identidade Visual</span></div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Cor primária</label>
                    <div style={{ display:'flex', gap:8 }}>
                      <input type="color" value={form.primary_color} onChange={e => set('primary_color', e.target.value)}
                        style={{ width:44, height:38, padding:2, cursor:'pointer', borderRadius:6 }} />
                      <input value={form.primary_color} onChange={e => set('primary_color', e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Cor secundária</label>
                    <div style={{ display:'flex', gap:8 }}>
                      <input type="color" value={form.secondary_color} onChange={e => set('secondary_color', e.target.value)}
                        style={{ width:44, height:38, padding:2, cursor:'pointer', borderRadius:6 }} />
                      <input value={form.secondary_color} onChange={e => set('secondary_color', e.target.value)} />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>Logo (upload)</label>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ padding:7 }} />
                  {uploading && <p className="text-muted mt-1">Enviando...</p>}
                </div>
                <div style={{ background:form.primary_color, borderRadius:10, padding:'14px 16px', display:'flex', alignItems:'center', gap:12, marginTop:4 }}>
                  {form.logo_url
                    ? <img src={form.logo_url} alt="" style={{ height:32, objectFit:'contain', borderRadius:6 }} />
                    : <div style={{ width:32, height:32, background:'rgba(255,255,255,0.25)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, color:'white', fontSize:14 }}>{form.name?.[0]||'A'}</div>
                  }
                  <div>
                    <div style={{ color:'white', fontWeight:700, fontSize:14 }}>{form.name || 'Nome da Empresa'}</div>
                    <div style={{ color:'rgba(255,255,255,0.65)', fontSize:11 }}>{form.slug || 'slug-empresa'}</div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginBottom:24 }}>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/master/tenants')}>Cancelar</button>
              <button type="submit" className={`btn btn-primary${loading?' btn-loading':''}`} disabled={loading}>
                {loading ? 'Salvando...' : isEdit ? 'Salvar Alterações' : 'Criar Cliente'}
              </button>
            </div>
          </div>
        </div>
      </form>

      {isEdit && (
        <div className="info-box" style={{ marginBottom:24 }}>
          Para gerenciar <strong>perfis & permissões</strong>, <strong>widget</strong> e <strong>integrações</strong> deste cliente, acesse{' '}
          <Link to={`/master/tenants/${id}`} style={{ fontWeight:700 }}>← Detalhes do cliente</Link>.
        </div>
      )}
    </div>
  );
}
